import { useRef, forwardRef, ForwardedRef } from 'react';
import { Typography } from '@mui/material';
import { usePageTitle } from 'hooks';
import SingleAccordion from 'components/SingleAccordion';
import Layout from 'layouts';
import { useScrollTo } from 'hooks/useScrollTo';
import { pushAnalyticsEvent } from 'helpers/analytics';
import BulletPointsList from 'components/BulletPointsList';
import { howItWorksPageContent } from './HowItWorks.copy';

import {
  StyledContainer,
  Section,
  StyledSingleAccordion,
  StyledMarkdown,
} from './HowItWorks.styles';

const { howItWorks, faqs } = howItWorksPageContent;

interface HeaderProps {
  id: string;
  title: string;
  subtitle: string;
}

const Header = forwardRef(
  ({ id, title, subtitle }: HeaderProps, ref: ForwardedRef<HTMLHeadingElement>) => (
    <>
      <Typography variant="h1" id={id} gutterBottom ref={ref}>
        {title}
      </Typography>
      {subtitle && <Typography gutterBottom>{subtitle}</Typography>}
    </>
  )
);

const Content = forwardRef((_, ref: ForwardedRef<HTMLHeadingElement>) => (
  <Section>
    <Header ref={ref} {...howItWorks} />
    <BulletPointsList content={howItWorks.content} />
  </Section>
));

const handleSectionOpen = (faqCategory: string, faqCopy: string) => {
  pushAnalyticsEvent({ event: 'faqClick', faqCategory, faqCopy });
};

const FaqList = forwardRef((_, ref: ForwardedRef<HTMLHeadingElement>) => (
  <>
    <Section>
      <Header ref={ref} {...faqs} />
    </Section>
    {faqs.content.map(({ name, questions }) => (
      <StyledSingleAccordion key={name} title={name} square>
        {questions.map(({ question, answer }) => (
          <SingleAccordion
            key={question}
            title={question}
            callback={() => handleSectionOpen(name, question)}
          >
            <StyledMarkdown>{answer}</StyledMarkdown>
          </SingleAccordion>
        ))}
      </StyledSingleAccordion>
    ))}
  </>
));

const sections = [
  {
    id: howItWorks.id,
    component: Content,
  },
  {
    id: faqs.id,
    component: FaqList,
  },
];

const HowItWorks = ({ hideHeader = false }: { hideHeader?: boolean }) => {
  usePageTitle('How it works');

  const sectionsRefs = useScrollTo(useRef<HTMLHeadingElement[]>([]), { tolerance: 100 });

  const addRef = (component: HTMLHeadingElement, idx: number) => {
    sectionsRefs.current[idx] = component;
  };

  return (
    <Layout ariaLabel="How it works page" hideHeader={hideHeader}>
      <StyledContainer>
        {sections.map((section, idx) => {
          const { id, component: Component } = section;

          return (
            <Component key={id} ref={(component: HTMLHeadingElement) => addRef(component, idx)} />
          );
        })}
      </StyledContainer>
    </Layout>
  );
};

export default HowItWorks;
